import { Mic } from 'lucide-react';

const AUDIO_LEVEL_BASE_HEIGHT_PX = 1;
const AUDIO_LEVEL_HEIGHT_SCALE = 17;
const LOUD_LEVEL_THRESHOLD = 1;
const QUIET_BAR_VARIATION = [0, 3, 6, 9, 6, 3, 0];
const LOUD_BAR_VARIATION = [0, 6, 12, 18, 12, 6, 0];

const IDLE_TITLE = 'Click to start recording';
const RECORDING_TITLE = 'Click to stop recording';
const TRANSCRIBING_TITLE = 'Transcribing...';

const IDLE_COLOUR_CLASSES =
  'bg-blue-500 text-white hover:bg-blue-600 hover:scale-105 active:scale-95 shadow-blue-200';
const RECORDING_COLOUR_CLASSES = 'bg-red-500 text-white scale-110 shadow-red-200';
const TRANSCRIBING_COLOUR_CLASSES = 'bg-gray-300 text-gray-500 cursor-not-allowed';

export interface MicButtonVisualProps {
  isRecording: boolean;
  isTranscribing: boolean;
  /** Per-bar waveform heights shown while recording, same shape `useVoiceCapture` produces. */
  audioLevels: number[];
  /** Present -> renders as the clickable start/stop control VoiceOrTextInput has always had.
   *  Omitted -> a purely decorative, non-interactive display: RapidFireVoiceRunner has no manual
   *  mic control (spec-02's "no Continue button"), so it must not gain a dead-click button here. */
  onToggle?: () => void;
}

/**
 * The mic button chrome every voice control in the survey shares: idle/recording colours, the
 * halo, the live waveform, the icon, and the transcribing spinner.
 *
 * Extracted verbatim out of VoiceOrTextInput.tsx (product-owner note: "even the mic icon and
 * animation in voice input and in rapid fire looks completely different" — stop inventing new UI
 * when the product already has one). VoiceOrTextInput keeps the exact same markup and behaviour
 * through this component; RapidFireVoiceRunner reuses it as a static display and layers its own
 * silence-countdown ring around it.
 */
export function MicButtonVisual({
  isRecording,
  isTranscribing,
  audioLevels,
  onToggle,
}: MicButtonVisualProps) {
  const colourClasses = isRecording
    ? RECORDING_COLOUR_CLASSES
    : isTranscribing
      ? TRANSCRIBING_COLOUR_CLASSES
      : IDLE_COLOUR_CLASSES;

  const body = isTranscribing ? (
    <div className="w-8 h-8 border-4 border-gray-500 border-t-transparent rounded-full animate-spin" />
  ) : isRecording ? (
    <div className="flex items-center justify-center space-x-0.5 h-12 w-12 relative">
      {audioLevels.map((level, i) => {
        const baseHeight = AUDIO_LEVEL_BASE_HEIGHT_PX + level * AUDIO_LEVEL_HEIGHT_SCALE;
        const variation =
          level < LOUD_LEVEL_THRESHOLD ? QUIET_BAR_VARIATION[i] : LOUD_BAR_VARIATION[i];
        const dynamicHeight = baseHeight + variation * level;
        return (
          <div
            key={i}
            className="w-1.5 bg-white rounded-full opacity-95 transition-all duration-75 ease-out"
            style={{ height: `${dynamicHeight}px` }}
          />
        );
      })}
    </div>
  ) : (
    <Mic className="w-8 h-8" />
  );

  return (
    <div className="relative">
      {!isRecording && !isTranscribing && (
        <div className="absolute inset-0 w-24 h-24 rounded-full bg-blue-400 animate-ping opacity-20" />
      )}
      {isRecording && (
        <div className="absolute inset-0 w-24 h-24 rounded-full bg-red-400 animate-pulse opacity-30" />
      )}

      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          disabled={isTranscribing}
          className={`relative w-24 h-24 rounded-full transition-all duration-300 flex items-center justify-center shadow-lg ${colourClasses}`}
          title={isRecording ? RECORDING_TITLE : isTranscribing ? TRANSCRIBING_TITLE : IDLE_TITLE}
        >
          {body}
        </button>
      ) : (
        <div
          aria-hidden="true"
          className={`relative w-24 h-24 rounded-full transition-all duration-300 flex items-center justify-center shadow-lg ${colourClasses}`}
        >
          {body}
        </div>
      )}
    </div>
  );
}
